import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import SingleTeam from "../../components/Team/SingleTeam";
import config from "../../config";
import makeRequest from "../../makeRequest";

const BASE = config.api.baseUrl;

function DoctorSlide({ doctor }) {
  const [imgSrc, setImgSrc] = useState(null);
  const urlRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${BASE}/website/doctor/${doctor.id}/image`)
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (blob && !cancelled) {
          const url = URL.createObjectURL(blob);
          urlRef.current = url;
          setImgSrc(url);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  }, [doctor.id]);

  return (
    <SingleTeam
      teamImage={imgSrc}
      titleClass="hover-anim tertiary"
      authorName={doctor.name}
      specialist={doctor.websiteSpeciality?.name || doctor.speciality}
      eduTagLine={doctor.educationTagLine}
      doctorId={doctor.id}
    />
  );
}

const Team = () => {
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchInputRef = useRef(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  useEffect(() => {
    makeRequest({ route: "/website/doctor?noPagination=1", method: "get" })
      .then(({ data }) => {
        if (Array.isArray(data)) setDoctors(data);
      })
      .catch(() => {});
  }, []);

  const handleOpenSearch = () => {
    setSearchOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 50);
  };

  const handleCloseSearch = () => {
    setSearchOpen(false);
    setQuery("");
  };

  const filtered = useMemo(() => {
    if (!query.trim()) return doctors;
    const q = query.trim().toLowerCase();
    return doctors.filter((d) => {
      const spec = d.websiteSpeciality?.name || d.speciality || "";
      return `${d.name} ${spec} ${d.designation || ""}`.toLowerCase().includes(q);
    });
  }, [doctors, query]);

  return (
    <div id="it-team" className="it-team-area p-relative fix z-index it-team-style-3 pb-120">
      {/* <div className="it-team-shape-1 d-none d-lg-block">
        <img src={shapeImg} alt="" />
      </div> */}
      <div className="container">
        {/* Header row with title + search/view-all */}
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 16, marginBottom: 48 }}>
          <div>
            <span className="mh-lf-section__eyebrow">
              <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M10 2a1 1 0 011 1v5h5a1 1 0 010 2h-5v5a1 1 0 01-2 0v-5H4a1 1 0 010-2h5V3a1 1 0 011-1z" />
              </svg>
              Experienced doctors. Personalized care
            </span>
            <h2 className="mh-lf-section__title" style={{ marginBottom: 0 }}>
              Our Expert Doctors
            </h2>
          </div>

          {/* Controls */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
            {/* Expandable search bar */}
            <div className={`mh-spec-search-wrap${searchOpen ? " mh-spec-search-wrap--open" : ""}`}>
              <svg
                className="mh-spec-search-wrap__icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                ref={searchInputRef}
                className="mh-spec-search-wrap__input"
                type="text"
                placeholder="Search by name, specialty…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <button className="mh-spec-search-wrap__close" onClick={handleCloseSearch} aria-label="Close search">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {!searchOpen && (
              <>
                <button
                  className="ma-btn-primary"
                  style={{ borderRadius: 8, fontSize: ".875rem", padding: "10px 20px", cursor: "pointer" }}
                  onClick={() => navigate("/our-doctors")}
                >
                  <i className="fa-regular fa-user" style={{ marginRight: 7 }}></i>
                  View All Doctors
                </button>

                <button className="mh-spec-search-btn" onClick={handleOpenSearch} aria-label="Search doctors">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>

        <div className="row">
          <div className="col-xl-12">
            {/* ── Filtered grid when searching ── */}
            {searchOpen ? (
              filtered.length === 0 ? (
                <div className="mh-spec-empty">
                  <svg
                    width="38"
                    height="38"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  <p>
                    No doctors match "<strong>{query}</strong>"
                  </p>
                </div>
              ) : (
                <div className="mh-spec-grid">
                  {filtered.map((doc) => (
                    <DoctorSlide key={doc.id} doctor={doc} />
                  ))}
                </div>
              )
            ) : (
              /* ── Normal carousel ── */
              <div className="it-team-wrapper">
                <div className="swiper-container it-team-active">
                  {doctors.length > 0 && (
                    <Swiper
                      modules={[Autoplay, Navigation]}
                      speed={800}
                      loop={doctors.length > 4}
                      slidesPerView={4}
                      spaceBetween={24}
                      grabCursor={true}
                      autoplay={{
                        delay: 3000,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true,
                      }}
                      breakpoints={{
                        1200: { slidesPerView: 4 },
                        992: { slidesPerView: 3 },
                        768: { slidesPerView: 2 },
                        0: { slidesPerView: 1 },
                      }}
                      onBeforeInit={(swiper) => {
                        swiper.params.navigation.prevEl = prevRef.current;
                        swiper.params.navigation.nextEl = nextRef.current;
                      }}
                    >
                      {doctors.map((doc) => (
                        <SwiperSlide key={doc.id}>
                          <DoctorSlide doctor={doc} />
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Nav buttons — carousel mode only */}
        {!searchOpen && (
          <div className="it-team-nav text-center mt-40">
            <button ref={prevRef} className="it-team-nav__btn" aria-label="Previous">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <span className="it-team-nav__swipe">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 8V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h8" />
                <path d="M10 16l4-4-4-4" />
                <path d="M16 16l4-4-4-4" />
              </svg>
              Swipe
            </span>
            <button ref={nextRef} className="it-team-nav__btn" aria-label="Next">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 6 15 12 9 18" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Team;
